import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLocation, getSearchLocation } from '../../../redux/location/actions/location.action';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { DEBOUNCE_DELAY } from '../../../constants/constants';
import LocationsHeader from './locations-header/LocationsHeader';
import LocationSearchBox from './search-box/LocationSearchBox';
import LocationsList from './locations-list/LocationsList';
import Toast from '../../common/toast/Toast';
import styles from './LocationsForNewLocationAvailability.module.css';

interface LocationState {
    type?: string;
}

/**
 * Optimized Locations Selection Component for New Location Availability Flow
 * 
 * Features:
 * - Proper error and loading state management
 * - Debounced search functionality
 * - Memoized callbacks for performance
 * - Error handling with user-friendly messages
 */
const LocationsForNewLocationAvailability: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const locationState = useLocation().state as LocationState | null;

    const type = locationState?.type || 'add-availability';
    const [searchText, setSearchText] = useState('');
    const [isClicked, setIsClicked] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);

    // Memoized debounced search handler
    const handleSearch = useCallback(
        (searchValue: string) => {
            try {
                if (searchValue.trim() !== '') {
                    dispatch(getSearchLocation(searchValue));
                    setError(null);
                } else {
                    if (isClicked) {
                        dispatch(getLocation(undefined, undefined, true));
                        setError(null);
                    }
                }
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to search locations';
                setError(errorMessage);
                setShowToast(true);
            }
        },
        [dispatch, isClicked]
    );

    // Track if initial load has been done
    const initialLoadRef = React.useRef(false);
    const locationData = useSelector((state: IRootState) => state.locationReducer?.data);
    const locationLoading = useSelector((state: IRootState) => state.locationReducer?.loading);

    // Initial load on mount - only once, and only if data doesn't exist
    useEffect(() => {
        const hasLocationData = locationData?.locations?.length > 0 || locationData?.locationinvitedlist?.length > 0;
        if (!initialLoadRef.current && !searchText && !hasLocationData && !locationLoading) {
            dispatch(getLocation(undefined, undefined, true));
            initialLoadRef.current = true;
        }
    }, [dispatch, searchText, locationData, locationLoading]);

    // Debounced search effect
    useEffect(() => {
        if (!searchText.trim() && !isClicked) {
            return; // Don't search if search is empty and not clicked
        }

        const delayDebounceFn = setTimeout(() => {
            handleSearch(searchText);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(delayDebounceFn);
    }, [searchText, handleSearch, isClicked]);

    // Auto-hide toast after 4 seconds
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // Handle keyboard input with validation
    const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ' ' && searchText.length === 0) {
            event.preventDefault();
        }
    }, [searchText]);

    // Handle input change with error clearing
    const handleInputChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const { value } = e.target as HTMLInputElement;
        setSearchText(value);
        setIsClicked(true);
        setError(null);
    }, []);

    return (
        <div className={`outletConainer ${styles.locationsContainer}`}>
            <LocationsHeader type={type} />
            <LocationSearchBox
                searchText={searchText}
                onKeyPress={handleKeyPress}
                onChange={handleInputChange}
            />
            <LocationsList type={type} />
            {showToast && error && (
                <Toast type="error" message={error} />
            )}
        </div>
    );
};

export default LocationsForNewLocationAvailability;

